import { useEffect, type ReactNode } from "react";
import {
  Links,
  Meta,
  Outlet,
  redirect,
  Scripts,
  ScrollRestoration,
  useFetcher,
  useLoaderData,
  useLocation,
  data,
} from "react-router";
import type {
  HeadersFunction,
  LinksFunction,
  LoaderFunctionArgs,
  MetaFunction,
} from "react-router";
import { AppProviders } from "~/providers/app-providers";
import { ClientHintCheck, getHints } from "~/lib/utils/client-hints";
import { combineHeaders, getDomainUrl } from "~/lib/utils/misc";
import { getEnv } from "~/lib/utils/env.server";
import { BRAND_NAME, PUBLIC_ROUTES } from "~/lib/utils/constants";
import { honeypot } from "~/lib/utils/honeypot.server";
import { type Theme, getTheme } from "~/lib/utils/theme.server";
import { getToast } from "~/lib/utils/toast.server";
import { useNonce } from "~/lib/utils/nonce-provider";
import { useTheme } from "~/routes/resources+/theme-switch";
import { useToast } from "~/components/toaster";
import { HoneypotProvider } from "remix-utils/honeypot/react";
import { GeneralErrorBoundary } from "~/components/error-boundary";
import { href as iconsHref } from "~/components/ui/icon";
import { Layout } from "./components/layouts";
import {
  getIsValhallaUser,
  getTenantFromRequest,
  getUserId,
  logout,
  requireTeamAuth,
} from "./lib/utils/auth.server";
import { db } from "./lib/db";
import { Toaster } from "./components/ui/sonner";
import {
  type User,
  type Team,
  type TeamMember,
  teamMembers,
  teams,
} from "./lib/db/schema";
import { createShouldRevalidate } from "./lib/utils/revalidation";
import { eq } from "drizzle-orm";
import "~/styles/globals.css";
import "~/styles/fonts.css";
import "~/styles/maps.css";
import "~/styles/custom.css";
import "~/styles/animations.css";
import { useDetectTenantDev } from "./lib/hooks/use-detect-tenant-dev";
import { getTenantUrl } from "./components/tenant-link";
import { getUserContext } from "./lib/utils/session.server";
import { getUserTimezone } from "./lib/utils/timezone";
import {
  cacheUserData,
  getCachedUserData,
  type CachedUserData,
} from "./lib/utils/session-cache.server";

export function TimezoneManager() {
  const fetcher = useFetcher();

  useEffect(() => {
    // Set the timezone cookie on client-side only
    const timezone = getUserTimezone();

    // Only send if we have a timezone
    if (timezone) {
      fetcher.submit({ timezone }, { action: "/set-timezone", method: "post" });
    }
  }, []);

  return null;
}

export const links: LinksFunction = () => {
  return [
    // Preload svg sprite as a resource to avoid render blocking
    { rel: "preload", href: iconsHref, as: "image" },

    // DNS prefetch for external APIs - resolves DNS early, no functional impact
    { rel: "dns-prefetch", href: "https://maps.googleapis.com" },
    { rel: "dns-prefetch", href: "https://maps.gstatic.com" },
    { rel: "dns-prefetch", href: "https://www.banxico.org.mx" },
    { rel: "dns-prefetch", href: "https://api.resend.com" },

    // Prefetch high-probability routes (React Router 7 compatible)
    // Using 'prefetch' instead of 'modulepreload' for route pages
    { rel: "prefetch", href: "/dashboard" },
    { rel: "prefetch", href: "/orders/purchases" },
    { rel: "prefetch", href: "/orders/sales" },
    { rel: "prefetch", href: "/inventories" },
    { rel: "prefetch", href: "/prices" },

    // Note: Dynamic terminal prefetching would need to be implemented differently
    // as LinksFunction doesn't have access to loader data in React Router 7
    // Consider using clientLoader or programmatic prefetching instead
  ].filter(Boolean);
};

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  return [
    { title: data ? BRAND_NAME : `Error | ${BRAND_NAME}` },
    // { name: "description", content: `Your own captain's log` },
  ];
};

/**
 * Optimized user query with Redis caching
 * Tries Redis first (sub-millisecond), falls back to DB
 * This is critical path data that blocks initial render
 */
const getUserById = async (
  userId: string
): Promise<CachedUserData | null> => {
  // Try Redis cache first (fast path)
  const cached = await getCachedUserData(userId);
  if (cached) {
    return cached;
  }

  // Fall back to database (slow path)
  const user = await db.query.users.findFirst({
    where: (user, { eq }) => eq(user.id, userId),
    columns: {
      id: true,
      email: true,
      username: true,
      createdAt: true,
    },
    with: {
      profile: {
        columns: {
          id: true,
          name: true,
          fallbackGradient: true,
        },
      },
      image: {
        columns: {
          id: true,
        },
      },
      role: {
        columns: {
          name: true,
        },
      },
      teamMember: {
        columns: {
          id: true,
          teamId: true,
        },
        with: {
          teamRole: {
            columns: {
              name: true,
            },
          },
        },
      },
    },
  });

  // Cache for future requests (fire and forget - don't block)
  if (user) {
    cacheUserData(userId, user as CachedUserData).catch(() => {});
  }

  return user as CachedUserData | null;
};

const getBaseLoaderData = async (request: Request) => {
  const { toast, headers: toastHeaders } = await getToast(request);
  const url = new URL(request.url);

  return {
    toast,
    toastHeaders,
    honeyProps: honeypot.getInputProps(),
    requestInfo: {
      hints: getHints(request),
      origin: getDomainUrl(request),
      path: url.pathname,
      userPrefs: {
        theme: getTheme(request),
      },
    },
    ENV: getEnv(),
  };
};

export async function loader({ request, ...rest }: LoaderFunctionArgs) {
  const url = new URL(request.url);
  const path = url.pathname;
  const tenant = getTenantFromRequest(request);
  const host = request.headers.get("host") || "";
  const isLocalDev =
    process.env.NODE_ENV === "development" && host.includes("localhost");
  const isVercelPreview = host.includes("vercel.app");
  const isDev = isLocalDev || isVercelPreview;

  const isPublicRoute = PUBLIC_ROUTES.includes(path);

  // Handle tenant routing based on environment
  const userId = await getUserId(request);

  const isValhallaUser = getIsValhallaUser(userId);

  // Handle tenant routing when user is logged in but no tenant in URL
  if (userId && !tenant && !isPublicRoute) {
    // Get user context from session cookie (fast - no DB/Redis call)
    const userContext = await getUserContext(request);

    // Try session cookie first (set during login), then fallback to DB
    let tenantName = userContext?.tenant || null;

    if (!tenantName) {
      // Fallback: query database for user's team (only on first visit after login)
      const defaultTeam = await db.query.teamMembers.findFirst({
        where: eq(teamMembers.userId, userId),
        with: { team: { columns: { slug: true } } },
      });
      tenantName = defaultTeam?.team?.slug || null;
    }

    if (tenantName) {
      // For development: modify the URL without redirect
      if (isDev) {
        url.searchParams.set("tenant", tenantName);
        const newRequest = new Request(url.toString(), request);
        return await loader({ request: newRequest, ...rest });
      }

      // For production: redirect to subdomain
      if (!isDev && !url.hostname.startsWith(`${tenantName}.`)) {
        const subdomain = new URL(url.toString());
        subdomain.hostname = `${tenantName}.${subdomain.hostname
          .split(".")
          .slice(-2)
          .join(".")}`;
        return redirect(subdomain.toString());
      }
    }
  }

  // Handle public routes
  if (isPublicRoute) {
    const initialData: {
      user: User | null;
      teamMember: TeamMember | null;
      team: Team | null;
    } = {
      user: null,
      teamMember: null,
      team: null,
    };

    if (userId) {
      const user = (await getUserById(userId)) as User;

      if (!user) {
        await logout({ request, redirectTo: "/" });
        throw redirect("/");
      }

      initialData.user = user;

      // Load team data if tenant exists
      if (tenant) {
        const team = (await db.query.teams.findFirst({
          where: eq(teams.slug, tenant),
          with: {
            images: true,
          },
        })) as Team;

        if (team) {
          initialData.team = team;
          const foundTeamMember = (await db.query.teamMembers.findFirst({
            where: (members, { and, eq }) =>
              and(eq(members.userId, userId), eq(members.teamId, team.id)),
            with: {
              teamRole: {
                columns: {
                  name: true,
                },
              },
            },
          })) as TeamMember;

          if (foundTeamMember) {
            initialData.teamMember = foundTeamMember;
          }
        }
      }
    }

    const { toast, toastHeaders, honeyProps, requestInfo, ENV } =
      await getBaseLoaderData(request);

    // Load terminals only if we have team context (defensive query to handle missing addresses)
    let terminals: { id: string; key: string; name: string; roadProductQuantity: string | null }[] = [];
    if (initialData.team) {
      try {
        terminals = await db.query.terminals.findMany({
          where: (terminal, { eq }) =>
            eq(terminal.teamId, initialData.team!.id),
          with: {
            address: {
              columns: {
                short: true,
                long: true,
                coordinates: true,
              },
            },
          },
          columns: {
            id: true,
            key: true,
            name: true,
            roadProductQuantity: true,
          },
        });
      } catch (error) {
        // Fallback query without address join if addresses are corrupted
        console.error(
          "Terminal address query failed, falling back to basic query:",
          error
        );
        terminals = await db.query.terminals.findMany({
          where: (terminal, { eq }) =>
            eq(terminal.teamId, initialData.team!.id),
          columns: {
            id: true,
            key: true,
            name: true,
            roadProductQuantity: true,
          },
        });
      }
    }

    return data(
      {
        ...initialData,
        requestInfo,
        ENV,
        toast,
        honeyProps,
        terminals,
        isDev,
        isValhallaUser,
      },
      {
        headers: combineHeaders(toastHeaders),
      }
    );
  }

  // Handle protected routes
  const {
    userId: loggedInUserId,
    team: partialTeam,
    teamMember,
  } = await requireTeamAuth(request);

  // Prefetch dashboard data since all authenticated users get redirected there
  // This starts loading the dashboard route and data early
  if (path === "/") {
    // No need to await, just start the import
    import("./routes/dashboard").catch(() => {}); // Ignore errors
  }

  // Start team query but don't await - will be streamed via Suspense
  // The logo/name can render after the page shell
  const teamPromise = Promise.resolve(
    db.query.teams.findFirst({
      where: (team, { eq }) => eq(team.id, partialTeam.id),
      with: {
        images: {
          columns: {
            id: true,
            type: true,
          },
        },
      },
    })
  );

  // Run critical queries in parallel - these block initial render
  // User data is cached in Redis for fast retrieval
  // Terminals must be awaited because useTerminals hook needs it synchronously
  const [user, terminals, { toast, toastHeaders, honeyProps, requestInfo, ENV }] =
    await Promise.all([
      getUserById(loggedInUserId),
      db.query.terminals.findMany({
        where: (terminal, { eq }) => eq(terminal.teamId, partialTeam.id),
        orderBy: (terminal, { asc }) => [asc(terminal.name)],
        with: {
          address: {
            columns: {
              short: true,
              long: true,
              coordinates: true,
            },
          },
        },
        columns: {
          id: true,
          key: true,
          name: true,
          roadProductQuantity: true,
        },
      }),
      getBaseLoaderData(request),
    ]);

  if (!user) {
    const redirectTo = getTenantUrl({
      to: "/",
      tenant: partialTeam.slug,
      isDev,
    });
    await logout({ request, redirectTo });
    throw redirect(redirectTo);
  }

  // Return data with deferred values
  // React Router 7 will stream teamPromise as it resolves
  return data(
    {
      user,
      teamPromise, // Streamed - doesn't block initial render
      teamMember,
      requestInfo,
      ENV,
      toast,
      honeyProps,
      terminals, // Awaited - useTerminals hook needs it synchronously
      isDev,
      isValhallaUser,
    },
    {
      headers: combineHeaders(toastHeaders),
    }
  );
}

export const headers: HeadersFunction = ({ loaderHeaders }) => {
  const headers = {
    "Server-Timing": loaderHeaders.get("Server-Timing") ?? "",
  };
  return headers;
};

const Document = ({
  children,
  nonce,
  theme = "light",
  env = {},
  allowIndexing = true,
}: {
  children: ReactNode;
  nonce?: string;
  theme?: Theme;
  env?: Record<string, string>;
  allowIndexing?: boolean;
}) => {
  // Only use nonce in production
  const nonceProps = nonce && process.env.NODE_ENV === 'production' ? { nonce } : {};
  
  return (
    <html lang="en" data-theme={theme} className={theme}>
      <head>
        <ClientHintCheck nonce={nonce} />
        <Meta />
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width,initial-scale=1" />
        {allowIndexing ? null : (
          <meta name="robots" content="noindex, nofollow" />
        )}
        <Links />
      </head>
      <body className="bg-background text-foreground">
        <TimezoneManager />
        {children}
        <script
          {...nonceProps}
          dangerouslySetInnerHTML={{
            __html: `window.ENV = ${JSON.stringify(env)}`,
          }}
        />
        <ScrollRestoration {...nonceProps} />
        <Scripts {...nonceProps} />
      </body>
    </html>
  );
};

function App() {
  const loaderData = useLoaderData<typeof loader>();
  const { ENV, toast, honeyProps } = loaderData;
  const teamPromise = "teamPromise" in loaderData ? loaderData.teamPromise : undefined;
  const nonce = useNonce();
  const theme = useTheme();
  const allowIndexing = ENV.ALLOW_INDEXING !== "false";

  const location = useLocation();
  const isTeamOnboarding = location.pathname.includes("/team-onboarding");
  const withLayout =
    isTeamOnboarding || !PUBLIC_ROUTES.includes(location.pathname);

  useToast(toast);

  // Include the tenant parameter on dev
  useDetectTenantDev();

  return (
    <HoneypotProvider {...honeyProps}>
      <Document
        nonce={nonce}
        theme={theme}
        allowIndexing={allowIndexing}
        env={ENV}
      >
        <AppProviders>
          {withLayout ? (
            <Layout teamPromise={teamPromise as any}>
              <Outlet />
            </Layout>
          ) : (
            <Outlet />
          )}
        </AppProviders>
        <Toaster position="top-center" theme={theme} />
      </Document>
    </HoneypotProvider>
  );
}

export const ErrorBoundary = () => {
  // the nonce doesn't rely on the loader so we can access that
  const nonce = useNonce();
  return (
    <Document nonce={nonce}>
      <GeneralErrorBoundary />
    </Document>
  );
};

export default App;

export const shouldRevalidate = createShouldRevalidate({
  ignoreParams: [
    "tab",
    "view",
    "terminalId",
    "teamName",
    "teamMember",
    "teamSlug",
    "slugError",
    "isAvailable",
    "checkSlug",
  ],
});
